import { useSyncExternalStore } from 'react';

import type { GuidanceOutcomeEvent } from './outcomes';
import type { GuidePageId } from './safeRoutes';
import type { GuidanceKey, GuideId } from './types';

/**
 * The in-memory state of guidance for one scope: whether the Help panel is
 * open, and the guide that is running, if any.
 *
 * Deliberately NOT persisted — that is `progress/`'s job, with its own rules.
 * `GuidanceRoot` makes one of these per scope, so a sign-out, an identity or
 * organisation switch, or a supplier's buyer change leaves no active guide
 * behind (CONTRACT §5).
 */
export type PauseReason =
  | 'target-missing'
  | 'dirty'
  | 'permission'
  | 'record'
  | 'error'
  /** The page changed under a routed step (browser Back, say): never fought. */
  | 'left-page';

/**
 * - `full`: the whole guide, from Help & tours — record steps included; off
 *   its record a record step pauses and explains.
 * - `page`: "Guide me on this page" — only this page's steps, never navigation.
 * - `orientation`: the first-visit "Show me around" (TOUR-02) — only the steps
 *   that need no record, so it reaches Finish on the portal's own pages; the
 *   full guide stays in progress at its first record step.
 * - `coach`: a guided workflow coach (Layer B, TASK §11) — the same engine,
 *   persistent across pages; it explains with the card, hands over to the
 *   person before every real action, and then WAITS in a non-modal dock for
 *   the authoritative outcome of what the person did.
 */
export type RunMode = 'full' | 'page' | 'orientation' | 'coach';

/** What a coach run keeps in memory (never persisted, never a record's data). */
export interface CoachRunState {
  /** The bus serial when the current step began: only later events count for it. */
  since: number;
  /** This run's outcome events, the latest few, oldest first. */
  observed: readonly GuidanceOutcomeEvent[];
  /**
   * Started by Continue — after a reload, Back or a new sign-in: the current
   * step is decided by its route and probe alone, never by an assumed outcome.
   */
  resumed: boolean;
  /** The dock is collapsed to its pill (the coach still follows along). */
  minimised: boolean;
  /** An application dialog is open: the coach hides and waits; it never stops it. */
  yielded: boolean;
  /**
   * The record page the PERSON opened during this run (memory only), so that
   * "Return to …" can take them back to the same record — never one the coach
   * chose.
   */
  bound: { page: GuidePageId; params: Readonly<Record<string, string>> } | null;
}

export interface ActiveRun {
  /** Distinguishes one start from the next, even of the same guide. */
  serial: number;
  /** The tour or, in `coach` mode, the coach. */
  tourId: GuideId;
  mode: RunMode;
  /** The page a `page` run explains; it never leaves it. */
  page: GuidePageId | null;
  stepId: string;
  /** Raised to resolve the current step again (Try again, a target that was replaced). */
  attempt: number;
  /**
   * Whether resolving the current step may navigate: only right after the
   * person moved (start, Next/Back, Try again) — never when the step is merely
   * resolved again because the page changed under it, which would fight the
   * browser's own history.
   */
  mayNavigate: boolean;
  /** Whether the card has been shown yet (until then, Help may still be opened). */
  shown: boolean;
  pause: { reason: PauseReason; explainKey?: GuidanceKey } | null;
  /** Where focus goes back to when the guide ends — the Help entry, usually. */
  opener: HTMLElement | null;
  /** `coach` mode only. */
  coach: CoachRunState | null;
}

export interface GuidanceState {
  panelOpen: boolean;
  active: ActiveRun | null;
}

export interface GuidanceStore {
  getState: () => GuidanceState;
  subscribe: (listener: () => void) => () => void;
  /** The shell's Help entry, where focus returns after a guide. */
  setHelpEntry: (element: HTMLElement | null) => void;
  helpEntry: () => HTMLElement | null;
  /**
   * Opens Help & tours. `returnFocusTo`: where focus goes when the panel closes
   * (and when a guide started from it ends) — the control that opened it from
   * a menu that has itself closed, say. The Help entry otherwise.
   */
  openPanel: (options?: { returnFocusTo?: HTMLElement | null }) => void;
  /** Where focus goes after the panel, as `openPanel` was asked; cleared once used. */
  takePanelReturn: () => HTMLElement | null;
  closePanel: () => void;
  start: (run: {
    tourId: GuideId;
    mode: RunMode;
    page?: GuidePageId | null;
    stepId: string;
    opener?: HTMLElement | null;
    /** Coach mode: started by Continue, so the step is decided by route and probe alone. */
    resumed?: boolean;
  }) => void;
  goTo: (stepId: string) => void;
  /**
   * A coach's step is done — by the person's own action — and the next one
   * begins where the person already is: no navigation.
   */
  advance: (stepId: string) => void;
  pause: (reason: PauseReason, explainKey?: GuidanceKey) => void;
  /** The person asked to try again: resolve the step anew, navigating if it must. */
  retry: () => void;
  /** The page changed under the step (its target went away): resolve it again, never navigating. */
  resolveAgain: () => void;
  /** The engine showed the card. */
  markShown: () => void;
  /** Ends the active guide, if any. Progress already written stays as it is. */
  stop: () => void;
  /** The outcome bus (WC-03): a tagged mutation changed state. Kept only for a running coach. */
  observe: (event: Omit<GuidanceOutcomeEvent, 'serial'>) => void;
  /** The coach dock collapses to its pill, or opens again. */
  setMinimised: (minimised: boolean) => void;
  /** An application dialog opened (true) or closed (false) over a running coach. */
  setYielded: (yielded: boolean) => void;
  /** The record page the person opened during this coach run (memory only). */
  bind: (page: GuidePageId, params: Readonly<Record<string, string>>) => void;
}

/** How many outcome events a coach run keeps: enough for a step, never a history. */
const OBSERVED_LIMIT = 12;

export function createGuidanceStore(): GuidanceStore {
  let state: GuidanceState = { panelOpen: false, active: null };
  let helpEntry: HTMLElement | null = null;
  let panelReturn: HTMLElement | null = null;
  let serial = 0;
  let busSerial = 0;
  const listeners = new Set<() => void>();

  const set = (next: GuidanceState) => {
    state = next;

    for (const listener of listeners) listener();
  };

  const updateActive = (change: (run: ActiveRun) => ActiveRun) => {
    if (state.active !== null) set({ ...state, active: change(state.active) });
  };

  // Only a real change notifies: the yield watcher asks on every DOM change.
  const updateCoach = (change: (coach: CoachRunState) => CoachRunState) => {
    const run = state.active;

    if (run === null || run.coach === null) return;

    const next = change(run.coach);

    if (next !== run.coach) set({ ...state, active: { ...run, coach: next } });
  };

  /** A coach step begins: only outcomes from now on count for it. */
  const freshStep = (run: ActiveRun): CoachRunState | null =>
    run.coach === null ? null : { ...run.coach, since: busSerial, resumed: false };

  return {
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    setHelpEntry: (element) => {
      helpEntry = element;
    },
    helpEntry: () => helpEntry,
    openPanel: (options = {}) => {
      if (state.panelOpen) return;

      panelReturn = options.returnFocusTo ?? null;

      // A guide still getting ready (its code loading, its target awaited) gives
      // way: its card must never appear under the Help panel's modal dialog.
      // A running coach stays: it yields to the panel and comes back after it.
      const pending = state.active !== null && !state.active.shown && state.active.coach === null;

      set({ panelOpen: true, active: pending ? null : state.active });
    },
    takePanelReturn: () => {
      const element = panelReturn;
      panelReturn = null;

      return element;
    },
    closePanel: () => {
      if (state.panelOpen) set({ ...state, panelOpen: false });
    },
    start: ({ tourId, mode, page, stepId, opener, resumed }) =>
      set({
        panelOpen: false,
        active: {
          serial: (serial += 1),
          tourId,
          mode,
          page: page ?? null,
          stepId,
          attempt: 0,
          mayNavigate: true,
          shown: false,
          pause: null,
          opener: opener ?? panelReturn ?? helpEntry,
          coach:
            mode === 'coach'
              ? {
                  since: busSerial,
                  observed: [],
                  resumed: resumed === true,
                  minimised: false,
                  yielded: false,
                  bound: null,
                }
              : null,
        },
      }),
    goTo: (stepId) =>
      updateActive((run) => ({
        ...run,
        stepId,
        pause: null,
        mayNavigate: true,
        coach: freshStep(run),
      })),
    advance: (stepId) =>
      updateActive((run) => ({
        ...run,
        stepId,
        pause: null,
        mayNavigate: false,
        coach: freshStep(run),
      })),
    pause: (reason, explainKey) =>
      updateActive((run) => ({
        ...run,
        pause: explainKey === undefined ? { reason } : { reason, explainKey },
      })),
    retry: () =>
      updateActive((run) => ({ ...run, pause: null, attempt: run.attempt + 1, mayNavigate: true })),
    resolveAgain: () =>
      updateActive((run) => ({
        ...run,
        pause: null,
        attempt: run.attempt + 1,
        mayNavigate: false,
      })),
    markShown: () => {
      if (state.active !== null && !state.active.shown)
        updateActive((run) => ({ ...run, shown: true }));
    },
    stop: () => {
      if (state.active !== null) set({ ...state, active: null });
    },
    observe: (event) => {
      busSerial += 1;

      const numbered: GuidanceOutcomeEvent = { ...event, serial: busSerial };

      updateCoach((coach) => ({
        ...coach,
        observed: [...coach.observed, numbered].slice(-OBSERVED_LIMIT),
      }));
    },
    setMinimised: (minimised) =>
      updateCoach((coach) => (coach.minimised === minimised ? coach : { ...coach, minimised })),
    setYielded: (yielded) =>
      updateCoach((coach) => (coach.yielded === yielded ? coach : { ...coach, yielded })),
    bind: (page, params) =>
      updateCoach((coach) =>
        coach.bound !== null &&
        coach.bound.page === page &&
        JSON.stringify(coach.bound.params) === JSON.stringify(params)
          ? coach
          : { ...coach, bound: { page, params: { ...params } } },
      ),
  };
}

export function useGuidanceState(store: GuidanceStore): GuidanceState {
  return useSyncExternalStore(store.subscribe, store.getState, store.getState);
}
